<template>
    <div class="attachment-container">
        <div :class="{
             removed,
             mini: miniView,
          }"
             class="attachment"
             @click="onDownload()">

            <checkbox v-if="canSelect"
                      v-model="checked"
                      class="checkbox"
                      @change="onInput" />

            <div class="icon">
                <component :is="icon" />
                <div class="file-ext">{{ fileExt.slice(0, 4) }}</div>
            </div>

            <div class="info">
                <span class="name">{{ fileName }}</span>
                <TooltipOld bind-to=".info .name"
                            class="hint"
                            trigger-on="hover">
                    {{ fileName }}
                    <span v-show="fileCreated" class="date">({{ fileCreated | dateFormat('rus.date') }})</span>
                </TooltipOld>

                <div class="params">
                    <span class="ext">{{ fileExt }}</span>
                    <span v-if="extAndSize" class="circle-marker"></span>
                    <span class="size">{{ fileSize }}</span>
                    <span v-if="fileTypeName" class="circle-marker"></span>
                    <span class="type">{{ fileTypeName }}</span>
                </div>
            </div>

            <div class="action-list">
                <Tooltip v-show="loadingError" by-hover class="tooltip-loading-error">
                    <template #target="{ on }">
                        <IconError v-on="on" />
                    </template>
                    <template #default="{ close }">
                        <slot :close="close" name="loading-error">Ошибка загрузки файла</slot>
                    </template>
                </Tooltip>

                <Tooltip v-show="showLoading" by-hover class="tooltip-loading">
                    <template #target="{ on }">
                        <IconLoader class="icon-loader" v-on="on" />
                    </template>
                    <template #default="{ close }">
                        <slot :close="close" name="loading">Идет загрузка файла</slot>
                    </template>
                </Tooltip>

                <div class="badges">
                    <template v-if="removed">
                        <div class="item error">Удалено</div>
                    </template>
                </div>

                <button v-show="canRestore && removed"
                        class="item restore only-icon no-border"
                        @click.stop="$emit('restore')">
                    <IconUndoLarge class="icon" />
                </button>
                <TooltipOld bind-to=".item.restore" class="tooltip hint" trigger-on="hover">
                    Восстановить
                </TooltipOld>

                <button v-if="actions" class="action only-icon no-border action-menu">
                    <IconMenu class="icon" />
                </button>
                <TooltipOld v-if="actions"
                            bind-to=".action-list .action-menu"
                            class="tooltip inform menu small"
                            trigger-on="click">

                    <button v-if="canMove" class="item iconed no-border move-menu">
                        <IconArchive class="icon" />
                        <span class="text">Переместить в папку</span>
                        <IconChevronRight class="icon chevron" />
                    </button>
                    <TooltipOld v-if="canMove"
                                bind-to=".action-list .item.move-menu"
                                class="tooltip inform menu small"
                                trigger-on="click">
                        <slot name="move-menu">Переместить в папку</slot>
                    </TooltipOld>

                    <button v-if="canEdit && !removed"
                            class="item iconed no-border"
                            @click.stop="$emit('edit')">
                        <IconEditLarge class="icon" />
                        <span class="text">Редактировать</span>
                    </button>

                    <button v-if="canRemove"
                            class="item remove iconed no-border"
                            @click.stop="$emit('remove')">
                        <IconTrashLarge class="icon" />
                        <span class="text">Удалить</span>
                    </button>
                </TooltipOld>
            </div>
        </div>
    </div>
</template>

<script>
import { formatFileSize, getFileName, getFileType } from '../../page_comp/Documents/fileApi.js';
import { toMap } from '@dsee/util/arrays.js';

import IconDocument from './icons/document.svg?component';
import IconDocumentMini from './icons/document_mini.svg?component';
import IconUndoLarge from './icons/undo_large.svg?component';
import IconLoader from './icons/loader.svg?component';
import IconError from './icons/error.svg?component';
import IconMenu from './icons/menu.svg?component';
import IconArchive from './icons/archive.svg?component';
import IconChevronRight from './icons/chevron-right.svg?component';


export default {
    name: 'AttachmentInfo',
    components: {
        IconDocument,
        IconUndoLarge,
        IconDocumentMini,
        IconLoader,
        IconError,
        IconMenu,
        IconArchive,
        IconChevronRight,
    },
    props: {
        value: {
            type: Object,
        },
        /**
         * Файл удален
         */
        removed: {
            type: Boolean,
            default: false,
        },
        /**
         * Можно удалить
         */
        canRemove: {
            type: Boolean,
            default: false,
        },
        /**
         * Можно восстановить файл
         */
        canRestore: {
            type: Boolean,
            default: false,
        },
        /**
         * Можно выбрать файл
         */
        canSelect: {
            type: Boolean,
            default: false,
        },
        /**
         * Можно редактировать файл
         */
        canEdit: {
            type: Boolean,
            default: false,
        },
        /**
         * Можно переместить файл
         */
        canMove: {
            type: Boolean,
            default: false,
        },
        /**
         * Мини представление
         */
        miniView: {
            type: Boolean,
            default: false,
        },
        /**
         * Файл загружается
         */
        loading: {
            type: Boolean,
            default: false,
        },
        /**
         * Ошибка загрузки файла
         */
        loadingError: {
            type: Boolean,
            default: false,
        },
    },
    inject: {
        typeDict: {
            from: 'documentTypeDict',
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            checked: false,
        };
    },
    computed: {
        fileName() {
            return getFileName(this.value.name) ?? '';
        },
        fileExt() {
            return getFileType(this.value.name) ?? '';
        },
        fileSize() {
            return formatFileSize(this.value.size).toUpperCase();
        },
        fileTypeName() {
            return this.value.type?.name ?? this.typeDictMap[this.value.type]?.name ?? '';
        },
        fileCreated() {
            return this.value.created;
        },
        actions() {
            if (this.removed)
                return false;

            return this.canRemove
                || this.canRestore
                || this.canSelect
                || this.canEdit
                || this.canMove;
        },
        extAndSize() {
            return this.fileExt && this.fileSize;
        },
        typeDictMap() {
            return toMap(this.typeDict);
        },
        icon() {
            return this.miniView ? 'IconDocumentMini' : 'IconDocument';
        },
        showLoading() {
            return this.loading && !this.loadingError;
        },
    },
    methods: {
        onDownload() {
            if (this.removed)
                return;

            this.$emit('download');

            // метрика
            try {
                this.$metrika.reachGoal('download_document');
            } catch (e) {
                console.error('Не удалось отправить метрику: ' + e);
            }
        },
        onInput($event) {
            this.$emit('checked', { ...this.value, checked: $event });
        },
    },
};
</script>

<style lang="scss" scoped>
.attachment-container {
    .attachment {
        display: flex;
        align-items: center;
        cursor: pointer;
        user-select: none;
        column-gap: 12px;
        box-sizing: border-box;
        width: 100%;

        & > .icon {
            display: flex;
            align-items: center;
            opacity: .7;
            transition: var(--transition-function);

            .file-ext {
                display: none;
            }
        }

        &.removed {
            .info {
                .name,
                .params {
                    color: var(--button-secondary-disabled-color);
                }
            }

            & > .icon {
                opacity: .25;
            }
        }

        .info {
            display: inline-grid;
            flex-direction: column;
            flex: none;
            flex-grow: 2;
            max-width: 100%;
            overflow: hidden;
            flex: auto;

            .name {
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
                margin-bottom: 2px;
                transition: var(--transition-function);
            }

            .params {
                display: flex;
                align-items: center;
                width: 100%;
                overflow: hidden;
                color: var(--label-color);
                font-size: 12px;
                line-height: 16px;
                transition: var(--transition-function);

                .ext {
                    text-transform: lowercase;
                    flex: none;
                }

                .size {
                    flex: none;
                }

                .circle-marker {
                    width: 2px;
                    height: 2px;
                    background-color: rgba(16, 24, 40, 0.5);
                    margin: 0 6px;
                }

                .type {
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                }
            }
        }

        .action-list {
            display: inline-flex;
            align-items: center;
            column-gap: 16px;

            .tooltip-loading {
                width: 16px;
                height: 16px;
            }

            .tooltip-loading-error {
                width: 24px;
                height: 24px;
            }

            .icon-loader {
                animation: spinner 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
            }
        }

        @keyframes spinner {
            0% {
                transform: rotate(0deg);
            }
            100% {
                transform: rotate(360deg);
            }
        }

        &.mini {
            .info {
                margin-left: 8px;

                .name {
                    color: var(--text-color)
                }
            }

            & > .icon {
                color: #8887F0;
                position: relative;

                .file-ext {
                    display: block;
                    position: absolute;
                    bottom: 0;
                    left: 4px;
                    white-space: nowrap;
                    font-size: 12px;
                    line-height: 12px;
                    text-transform: uppercase;
                }
            }
        }
    }
}

.tooltip-info {
    .date {
        color: rgba(255, 255, 255, 0.60);
    }
}

.tooltip.inform.menu {
    max-width: 232px;
    width: 100%;

    .item {
        width: 100%;
        font-weight: 500;

        &:not( :last-child) {
            margin-bottom: 14px;
        }

        .icon {
            color: rgba(131, 137, 151, 1);
        }
    }

    .move-menu {
        .chevron {
            margin: 0;
        }
    }
}
</style>
